/* supplies — what the job needs, what you are actually ordering, and the
   difference, shown rather than assumed.

   The supplies list was read-only: it printed the estimate's bill of materials
   and the only thing you could do was raise a purchase order for exactly that.
   Real ordering isn't exactly that. You buy a couple of spare panels because
   one arrives cracked, a bag or two of concrete over because a post hole runs
   deep, a whole extra post kit because the last run never measures the way the
   drawing says. And sometimes you deliberately order short, because there are
   six bags left on the truck from the last job.

   So the ordered quantity is editable, and the gap against the job is always on
   screen with its arithmetic spelled out — over, short, or exact — because
   "33" on its own tells you nothing about whether it is enough. */

export type BomLine = { label: string; qty: number };

export type SupplyLine = {
  label: string;
  /** what the estimate says this job consumes */
  need: number;
  /** what will actually be ordered */
  order: number;
  /** order − need: positive is spare, negative is short */
  delta: number;
  /** delta as a share of need, for the "+10%" style read */
  pct: number;
  state: "exact" | "spare" | "short";
  /** the arithmetic, in words */
  math: string;
};

const r0 = (n: any) => Math.max(0, Math.round(Number(n) || 0));

/** merge the estimate's BOM with any per-job order overrides */
export function supplyLines(bom: BomLine[], overrides: any): SupplyLine[] {
  const ov = (overrides && typeof overrides === "object") ? overrides : {};
  return (bom || []).map((b) => {
    const need = r0(b.qty);
    const order = ov[b.label] == null ? need : r0(ov[b.label]);
    const delta = order - need;
    const pct = need > 0 ? (delta / need) * 100 : 0;
    const state: SupplyLine["state"] = delta === 0 ? "exact" : delta > 0 ? "spare" : "short";
    const math = delta === 0
      ? `${order} ordered = ${need} the job needs`
      : delta > 0
      ? `${order} ordered − ${need} needed = ${delta} spare${need > 0 ? ` (+${pct.toFixed(0)}%)` : ""}`
      : `${order} ordered − ${need} needed = ${delta} short${need > 0 ? ` (${pct.toFixed(0)}%)` : ""}`;
    return { label: b.label, need, order, delta, pct, state, math };
  });
}

/** one line for the whole order: is it covered, and by how much */
export function supplySummary(lines: SupplyLine[]) {
  const short = lines.filter((l) => l.state === "short");
  const spare = lines.filter((l) => l.state === "spare");
  const totalNeed = lines.reduce((t, l) => t + l.need, 0);
  const totalOrder = lines.reduce((t, l) => t + l.order, 0);
  return {
    short, spare, totalNeed, totalOrder,
    covered: short.length === 0,
    // the honest headline: short beats spare, because being short stops a crew
    headline: short.length
      ? `${short.length} item${short.length === 1 ? "" : "s"} short of what the job needs`
      : spare.length
      ? `Covered, with spare on ${spare.length} item${spare.length === 1 ? "" : "s"}`
      : "Ordering exactly what the job needs",
  };
}

/** add a percentage of waste to every line, rounded up — the common case */
export function withWaste(bom: BomLine[], pct: number): any {
  const out: any = {};
  (bom || []).forEach((b) => {
    const need = r0(b.qty);
    out[b.label] = need > 0 ? Math.ceil(need * (1 + pct / 100)) : need;
  });
  return out;
}

/** strip overrides that match the need, so a cleared line stops being stored */
export function tidyOverrides(bom: BomLine[], overrides: any): any {
  const out: any = {};
  const byLabel: any = {};
  (bom || []).forEach((b) => { byLabel[b.label] = r0(b.qty); });
  Object.keys(overrides || {}).forEach((k) => {
    if (byLabel[k] == null) return;                 // line no longer on the job
    if (r0(overrides[k]) === byLabel[k]) return;     // same as the need: not an override
    out[k] = r0(overrides[k]);
  });
  return out;
}
